import {memo, useRef} from "react";
import {useSelector} from "react-redux";
import {css, cx} from "@panda/css";

import {Button} from "@pg-design/button-module";
import {BrandBuildingCubeIcon, BrandMapPinIcon, MapMarkerLocationIcon} from "@pg-design/icons-module";
import {Text} from "@pg-design/text-module";
import {OFFER_TEST_IDS} from "@pg-mono/test-ids";
import {useUserDevice} from "@pg-mono/user-device";
import {Country} from "@pg-rp/common-types";

import {rpFeatureFlagVendorReportDownload} from "../../app/read_rp_environment_variables";
import {IRPStore} from "../../app/rp_reducer";
import {OneColumnGridHolder, OneColumnGridMain} from "../../atoms/OneColumnGrid";
import {useStickyIntersection} from "../../hooks/use_sticky_intersection";
import {GTMEventApplicationSourceSection} from "../../lead/types/GTMEvenApplicationSourceSection";
import {VendorReportButton} from "../../vendor/components/VendorReportButton";
import {DetailConstructionGalleryCTA} from "../components/DetailConstructionGalleryCTA";
import {Planner3dModal} from "../detail/planner3D/components/Planner3D";
import {IOfferDetail} from "../types/IOfferDetail";
import {OfferModalActiveTab} from "../types/OfferModalActiveTab";

interface IProps {
    offer: IOfferDetail;
    onButtonClick: (tab: OfferModalActiveTab, source?: GTMEventApplicationSourceSection) => void;
    isPlanScriptLoading?: boolean;
    onPlansOpenClick?: () => void;
    className?: string;
}

export const OfferStickyBar = memo((props: IProps) => {
    const {offer, onButtonClick, isPlanScriptLoading, onPlansOpenClick, className} = props;

    const isAbroad = offer?.region.country !== Country.POLAND;

    const hasStages = useSelector((store: IRPStore) => !!store.offerDetail.offer?.groups?.stages.length);

    const wrapperRef = useRef<HTMLDivElement>(null);

    const {isMobile} = useUserDevice();
    const isSticky = useStickyIntersection(wrapperRef);

    return (
        <div className={cx(className, stickyBarWrapperStyle, isSticky && css({zIndex: 22}))} ref={wrapperRef}>
            <div className={cx(stickyBarStyle, isSticky && stickyBarActiveStyle)}>
                <OneColumnGridHolder>
                    <OneColumnGridMain className={colStyle}>
                        <div className={cx(barStyles, isSticky && barStylesActive)}>
                            <div className={cx(hiddenHeader, isSticky && !isMobile && showHeader)}>
                                <Text className="bt">{offer.name}</Text>

                                <address className={addressHolder}>
                                    <MapMarkerLocationIcon className={css({mr: 2})} fill="var(--colors-gray-700)" size="2.4" />

                                    <Text variant="body_copy_2" color="var(--colors-gray-700)" className="bt">
                                        {offer.address}
                                    </Text>
                                </address>
                            </div>

                            <div className={brandButtonsWrapperStyle}>
                                {rpFeatureFlagVendorReportDownload && !isAbroad && !isMobile && <VendorReportButton vendorId={offer.vendor.id} />}

                                <DetailConstructionGalleryCTA
                                    onButtonClick={() =>
                                        onButtonClick(
                                            OfferModalActiveTab.CONSTRUCTION_GALLERY,
                                            isSticky ? GTMEventApplicationSourceSection.BUTTON_STICKY_BAR : GTMEventApplicationSourceSection.BUTTON
                                        )
                                    }
                                />

                                {!isMobile && offer.configuration.show_offer_plans && isPlanScriptLoading != null && onPlansOpenClick != null && (
                                    <Button
                                        className={offerDetailBrandButtonStyle}
                                        iconLeft={BrandBuildingCubeIcon}
                                        variant="brand_icon"
                                        size={isMobile ? "medium" : "big"}
                                        onClick={onPlansOpenClick}
                                        isLoading={isPlanScriptLoading}
                                    >
                                        Plan pięter
                                    </Button>
                                )}

                                <Planner3dModal offer={offer} vendorId={offer.vendor.id} routedModalId="nav">
                                    <Button
                                        className={offerDetailBrandButtonStyle}
                                        iconLeft={BrandBuildingCubeIcon}
                                        variant="brand_icon"
                                        size={isMobile ? "medium" : "big"}
                                    >
                                        Plan pięter
                                    </Button>
                                </Planner3dModal>

                                {!hasStages && (
                                    <Button
                                        className={offerDetailBrandButtonStyle}
                                        iconLeft={BrandMapPinIcon}
                                        variant="brand_icon"
                                        size={isMobile ? "medium" : "big"}
                                        onClick={() => {
                                            onButtonClick(
                                                OfferModalActiveTab.MAP,
                                                isSticky ? GTMEventApplicationSourceSection.BUTTON_STICKY_BAR : GTMEventApplicationSourceSection.BUTTON
                                            );
                                        }}
                                        isLoading={isPlanScriptLoading}
                                        dataTestId={OFFER_TEST_IDS.OFFER_VIEW.OPEN_MAP_BUTTON}
                                    >
                                        Zobacz na mapie
                                    </Button>
                                )}
                            </div>
                        </div>
                    </OneColumnGridMain>
                </OneColumnGridHolder>
            </div>
        </div>
    );
});

const stickyBarWrapperStyle = css({
    padding: "0.8rem 0",

    md: {
        padding: "1.6rem 0",
        position: "sticky",
        top: "-1.6rem"
    }
});

const stickyBarStyle = css({
    padding: "0.8rem 0"
});

const stickyBarActiveStyle = css({
    md: {
        boxShadow: "var(--shadow-4)",
        backgroundColor: "var(--colors-white)"
    },

    "@media (min-width: 768px) and (max-width: 1310px)": {
        marginTop: 60
    }
});

const colStyle = css({
    lg: {
        maxWidth: "100%",
        flex: "1 0 0",
        ml: 0
    }
});

const barStyles = css({
    width: "100%",
    display: "flex",
    flexDirection: "column",
    justifyContent: "space-between",
    gap: "2.7rem"
});

const barStylesActive = css({
    flexDirection: "row"
});

const hiddenHeader = css({
    opacity: 0,
    display: "none"
});

const showHeader = css({
    display: "block",
    animationStyle: "fade-in"
});

const addressHolder = css({
    cursor: "pointer",
    display: "flex",
    alignItems: "center",

    md: {
        mt: 1
    }
});

const brandButtonsWrapperStyle = css({
    md: {
        display: "flex",
        justifyContent: "flex-end",
        ml: 3,
        gap: "2.7rem"
    }
});

export const offerDetailBrandButtonStyle = css({
    padding: 0,

    "&:only-child": {
        margin: 0
    }
});
import {memo, useRef} from "react";
import {useSelector} from "react-redux";
import {css, cx} from "@panda/css";

import {Button} from "@pg-design/button-module";
import {BrandBuildingCubeIcon, BrandMapPinIcon, MapMarkerLocationIcon} from "@pg-design/icons-module";
import {Text} from "@pg-design/text-module";
import {OFFER_TEST_IDS} from "@pg-mono/test-ids";
import {useUserDevice} from "@pg-mono/user-device";
import {Country} from "@pg-rp/common-types";

import {rpFeatureFlagVendorReportDownload} from "../../app/read_rp_environment_variables";
import {IRPStore} from "../../app/rp_reducer";
import {OneColumnGridHolder, OneColumnGridMain} from "../../atoms/OneColumnGrid";
import {useStickyIntersection} from "../../hooks/use_sticky_intersection";
import {GTMEventApplicationSourceSection} from "../../lead/types/GTMEvenApplicationSourceSection";
import {VendorReportButton} from "../../vendor/components/VendorReportButton";
import {DetailConstructionGalleryCTA} from "../components/DetailConstructionGalleryCTA";
import {Planner3dModal} from "../detail/planner3D/components/Planner3D";
import {IOfferDetail} from "../types/IOfferDetail";
import {OfferModalActiveTab} from "../types/OfferModalActiveTab";

interface IProps {
    offer: IOfferDetail;
    onButtonClick: (tab: OfferModalActiveTab, source?: GTMEventApplicationSourceSection) => void;
    isPlanScriptLoading?: boolean;
    onPlansOpenClick?: () => void;
    className?: string;
}

export const OfferStickyBar = memo((props: IProps) => {
    const {offer, onButtonClick, isPlanScriptLoading, onPlansOpenClick, className} = props;

    const isAbroad = offer?.region.country !== Country.POLAND;

    const hasStages = useSelector((store: IRPStore) => !!store.offerDetail.offer?.groups?.stages.length);

    const wrapperRef = useRef<HTMLDivElement>(null);

    const {isMobile} = useUserDevice();
    const isSticky = useStickyIntersection(wrapperRef);

    return (
        <div className={cx(className, stickyBarWrapperStyle, isSticky && css({zIndex: 22}))} ref={wrapperRef}>
            <div className={cx(stickyBarStyle, isSticky && stickyBarActiveStyle)}>
                <OneColumnGridHolder>
                    <OneColumnGridMain className={colStyle}>
                        <div className={cx(barStyles, isSticky && barStylesActive)}>
                            <div className={cx(hiddenHeader, isSticky && !isMobile && showHeader)}>
                                <Text className="bt">{offer.name}</Text>

                                <address className={addressHolder}>
                                    <MapMarkerLocationIcon className={css({mr: 2})} fill="var(--colors-gray-700)" size="2.4" />

                                    <Text variant="body_copy_2" color="var(--colors-gray-700)" className="bt">
                                        {offer.address}
                                    </Text>
                                </address>
                            </div>

                            <div className={brandButtonsWrapperStyle}>
                                {rpFeatureFlagVendorReportDownload && !isAbroad && !isMobile && <VendorReportButton vendorId={offer.vendor.id} />}

                                <DetailConstructionGalleryCTA
                                    onButtonClick={() =>
                                        onButtonClick(
                                            OfferModalActiveTab.CONSTRUCTION_GALLERY,
                                            isSticky ? GTMEventApplicationSourceSection.BUTTON_STICKY_BAR : GTMEventApplicationSourceSection.BUTTON
                                        )
                                    }
                                />

                                {!isMobile && offer.configuration.show_offer_plans && isPlanScriptLoading != null && onPlansOpenClick != null && (
                                    <Button
                                        className={offerDetailBrandButtonStyle}
                                        iconLeft={BrandBuildingCubeIcon}
                                        variant="brand_icon"
                                        size={isMobile ? "medium" : "big"}
                                        onClick={onPlansOpenClick}
                                        isLoading={isPlanScriptLoading}
                                    >
                                        Plan pięter
                                    </Button>
                                )}

                                <Planner3dModal offer={offer} vendorId={offer.vendor.id} routedModalId="nav">
                                    <Button
                                        className={offerDetailBrandButtonStyle}
                                        iconLeft={BrandBuildingCubeIcon}
                                        variant="brand_icon"
                                        size={isMobile ? "medium" : "big"}
                                    >
                                        Plan pięter
                                    </Button>
                                </Planner3dModal>

                                {!hasStages && (
                                    <Button
                                        className={offerDetailBrandButtonStyle}
                                        iconLeft={BrandMapPinIcon}
                                        variant="brand_icon"
                                        size={isMobile ? "medium" : "big"}
                                        onClick={() => {
                                            onButtonClick(
                                                OfferModalActiveTab.MAP,
                                                isSticky ? GTMEventApplicationSourceSection.BUTTON_STICKY_BAR : GTMEventApplicationSourceSection.BUTTON
                                            );
                                        }}
                                        isLoading={isPlanScriptLoading}
                                        dataTestId={OFFER_TEST_IDS.OFFER_VIEW.OPEN_MAP_BUTTON}
                                    >
                                        Zobacz na mapie
                                    </Button>
                                )}
                            </div>
                        </div>
                    </OneColumnGridMain>
                </OneColumnGridHolder>
            </div>
        </div>
    );
});

const stickyBarWrapperStyle = css({
    padding: "0.8rem 0",

    md: {
        padding: "1.6rem 0",
        position: "sticky",
        top: "-1.6rem"
    }
});

const stickyBarStyle = css({
    padding: "0.8rem 0"
});

const stickyBarActiveStyle = css({
    md: {
        boxShadow: "var(--shadow-4)",
        backgroundColor: "var(--colors-white)"
    },

    "@media (min-width: 768px) and (max-width: 1310px)": {
        marginTop: 60
    }
});

const colStyle = css({
    lg: {
        maxWidth: "100%",
        flex: "1 0 0",
        ml: 0
    }
});

const barStyles = css({
    width: "100%",
    display: "flex",
    flexDirection: "column",
    justifyContent: "space-between",
    gap: "2.7rem"
});

const barStylesActive = css({
    flexDirection: "row"
});

const hiddenHeader = css({
    opacity: 0,
    display: "none"
});

const showHeader = css({
    display: "block",
    animationStyle: "fade-in"
});

const addressHolder = css({
    cursor: "pointer",
    display: "flex",
    alignItems: "center",

    md: {
        mt: 1
    }
});

const brandButtonsWrapperStyle = css({
    md: {
        display: "flex",
        justifyContent: "flex-end",
        ml: 3,
        gap: "2.7rem"
    }
});

export const offerDetailBrandButtonStyle = css({
    padding: 0,

    "&:only-child": {
        margin: 0
    }
});
